import OfferPage from 'components/pages/Offer';

import {
  PRICE_COMPARATOR_REDESIGN_EXPERIMENT_ID,
  getServerExperimentProps,
} from 'lib/experiment';
import { toCategoryLink, toSubcategoryLink } from 'lib/links';
import { resolvify } from 'lib/promises';
import { isRefererFromBlog } from 'lib/url';

import { getAds } from 'services/ads';
import { getCategoriesList } from 'services/category';
import { getOfferCommentsList } from 'services/comment';
import { getCouponFeaturedStoresList } from 'services/coupon';
import { getLandingsList } from 'services/landing';
import {
  getOfferInfo,
  getOfferReviews,
  getOffersToCompareList,
  getRelatedOffers,
} from 'services/offer';
import { getStoresList } from 'services/store';

import APP from 'constants/app';
import COMMENT from 'constants/comment';
import LINKS from 'constants/links';
import OFFER from 'constants/offer';
import STATUS_CODES from 'constants/statusCodes';

const REVIEWS_LIMIT = 3;
const REVIEWS_SORT = 'relevance';

const Offer = (props) => <OfferPage {...props} />;

export const getServerSideProps = async ({
  params: { offerSlug },
  req,
  res,
  query,
  req: {
    headers: { referer },
  },
}) => {
  const { meliuzbit = null } = query;

  try {
    const [
      offer,
      offerReviews,
      serverComments,
      relatedOffers,
      offersToCompare,
      featuredStores,
      categories,
      stores,
      featuredLandings,
      highlightedLandings,
      {
        data: {
          offerAd: sidebarAds = [],
          topAd: [topbarAd = null],
          topbarHighlight: navbarAds,
        },
      },
    ] = await resolvify([
      getOfferInfo({
        offerId: offerSlug,
        meliuzbit,
      }),
      {
        fn: getOfferReviews({
          offerSlug,
          sort: REVIEWS_SORT,
          limit: REVIEWS_LIMIT,
        }),
        fallback: getOfferReviews.fallback,
      },
      {
        fn: getOfferCommentsList({
          limit: COMMENT.LIMIT,
          offerId: offerSlug,
          repliesLimit: COMMENT.REPLIES_LIMIT,
          sort: COMMENT.SORT_VALUES.RELEVANCE,
        }),
        fallback: [],
      },
      {
        fn: getRelatedOffers({
          limit: 4,
          offerId: offerSlug,
        }),
        fallback: getRelatedOffers.fallback,
      },
      {
        fn: getOffersToCompareList({ offerId: offerSlug }),
        fallback: getOffersToCompareList.fallback,
      },
      {
        fn: getCouponFeaturedStoresList(),
        fallback: getCouponFeaturedStoresList.fallback,
      },
      {
        fn: getCategoriesList({ selectedCategories: true }),
        fallback: getCategoriesList.fallback,
      },
      {
        fn: getStoresList({ from: 'topbar' }),
        fallback: getStoresList.fallback,
      },
      {
        fn: getLandingsList({ featured: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getLandingsList({ highlighted: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getAds(),
        fallback: getAds.fallback,
      },
    ]);

    if (offer.offerStatusName === OFFER.STATUSES.ARCHIVED) {
      const { categorySlug, subcategorySlug } = offer;

      return {
        redirect: {
          destination: subcategorySlug
            ? toSubcategoryLink(subcategorySlug)
            : toCategoryLink(categorySlug),
          permanent: false,
        },
      };
    }

    const reviews = offerReviews.data;
    const reviewsMetaItems = offerReviews.meta?.items;

    offer.totalReviews =
      offerReviews.data.length > 0
        ? reviewsMetaItems?.total && reviewsMetaItems !== null
          ? (reviewsMetaItems.total ?? 0)
          : Number(reviewsMetaItems) || 0
        : 0;

    return {
      props: {
        featuredStores,
        offerKey: offerSlug,
        offersToCompare: offersToCompare.data,
        isPromobitHasTheBestPrice:
          offersToCompare.meta.summary.offerHasTheBestPrice,
        relatedOffers: relatedOffers.data,
        reviews,
        serverComments,
        serverOffer: offer,
        sidebarAds,
        [APP.CTX_OPTIONAL_PROPS]: {
          categories,
          featuredLandings,
          highlightedLandings,
          navbarAds,
          stores,
          topbarAd,
          experiments: {
            ...getServerExperimentProps({
              expId: PRICE_COMPARATOR_REDESIGN_EXPERIMENT_ID,
              req,
              res,
            }),
          },
        },
      },
    };
  } catch ({ message }) {
    // Offer can be accessed but needs authentication..
    // Fetching will be done on client
    if (message === STATUS_CODES[401]) {
      return {
        props: {
          offerKey: offerSlug,
          reviews: [],
        },
      };
    }

    /**
     * Either offer can't be accessed or the request comes from our own blog
     * and probably is a offer that doesn't exist anymore. In both cases, we
     * should also redirect to the home page for UX/SEO purposes.
     */
    if (
      message === STATUS_CODES[403] ||
      (referer && isRefererFromBlog(referer))
    ) {
      return { redirect: { destination: LINKS.HOME, permanent: true } };
    }

    // Offer doesn't exist
    return { redirect: { destination: LINKS[404], permanent: false } };
  }
};

export default Offer;
